import React, { forwardRef, PropsWithChildren, ReactElement, useMemo } from 'react'
import clsx from 'clsx'
import { Tabs as TabsStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps } from '../helperTypes'

// TODO autoscroll to selected tab at init / on change

type Props = PropsWithChildren<{
  /** Floating variant without bottom line, and stretched to 100% height */
  floating?: boolean
  /** Callback fired when the value changes */
  onChange?: (event: React.MouseEvent<{}>, value: any) => void
  /** Makes Tabs scrollable on touch devices */
  scrollable?: boolean
  /**
   * The size of the tabs
   * @default medium
   */
  size?: 'medium' | 'large' | 'xlarge'
  /** The value of the currently selected `Tab` */
  value?: number | string
  /**
   * Change the look of the Tab and Tabs using variant
   * @default default
   */
  variant?: 'default' | 'container'
}>

type DeprecatedProps = {
  dark?: boolean
  secondary?: boolean
}

export type TabsProps = Props & DeprecatedProps & CommonProps

const useStyles = createUseStyles(TabsStyles, 'tabs')

export const Tabs = (props: TabsProps, ref: React.Ref<HTMLUListElement>) => {
  const { children, className, floating, onChange, secondary, scrollable, size, value, variant, ...rest } = props
  useDeprecatedProps('Tabs', { dark: undefined, secondary: undefined }, props)

  const classes = useStyles()

  const tabs = useMemo(() => {
    const valueToIndex = new Map()
    let childIndex = 0
    let isValueAssigned = false
    const tabs = React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) {
        return null
      }

      if (child.type === React.Fragment) {
        console.warn(
          "[AIRBUS-DS] The Tabs component doesn't accept a Fragment as a child. Consider providing an array instead.",
        )
      }

      const childProps = child.props as any
      const { value: childPropValue, ...restChildProps } = childProps
      const childValue = childPropValue === undefined ? childIndex : childPropValue
      valueToIndex.set(childValue, childIndex)
      const selected = childValue === value
      if (selected) isValueAssigned = true

      childIndex += 1
      return React.cloneElement<any>(child, {
        onChange,
        selected,
        size,
        variant,
        value: childValue,
        ...restChildProps,
      })
    })

    // if no tab selected (or value not found)
    // enable keyboard navigation on first tab by default
    if (!isValueAssigned && tabs?.length) {
      tabs[0] = React.cloneElement(tabs[0], { tabIndex: 0 })
    }

    return tabs
  }, [children, onChange, size, value, variant])

  const findNextItemToHighlight = (
    children: ReactElement[],
    currentIndex: number,
    direction: 'next' | 'prev',
  ): number => {
    const totalTabs = children.length
    const step = direction === 'next' ? 1 : -1

    for (let i = 1; i <= totalTabs; i++) {
      const newIndex = (currentIndex + step * i + totalTabs) % totalTabs
      if (!children[newIndex]?.props?.disabled) {
        return newIndex
      }
    }

    return currentIndex
  }

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
    const { currentTarget, code, target } = evt
    const tabs: NodeListOf<HTMLElement> = (currentTarget as HTMLElement)?.querySelectorAll('[tabIndex]')
    const focusedIndex = Array.from(tabs).indexOf(target as HTMLElement)
    const reactChildren = React.Children.toArray(children)

    // prevent default native behaviour for managed keys
    if (['ArrowRight', 'ArrowLeft', 'Enter', 'Space'].includes(code)) {
      evt.preventDefault()
    }

    switch (code) {
      case 'ArrowRight':
      case 'ArrowLeft':
        tabs[
          findNextItemToHighlight(
            reactChildren as ReactElement[],
            focusedIndex,
            code === 'ArrowRight' ? 'next' : 'prev',
          )
        ]?.focus()
        break

      case 'Enter':
      case 'Space':
        tabs[focusedIndex].click()
        break

      default:
        break
    }
  }

  const Wrapper = scrollable ? 'div' : React.Fragment
  // We can't pass a className prop to a fragment
  const wrapperProps = scrollable ? { className: clsx(scrollable && classes.scrollwrapper) } : {}
  return (
    <Wrapper {...wrapperProps}>
      <ul
        ref={ref}
        className={clsx(
          className,
          classes.root,
          floating && classes._floating,
          variant === 'container' && classes[`_${variant}`],
        )}
        role='tablist'
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {tabs}
      </ul>
    </Wrapper>
  )
}

export default forwardRef<HTMLUListElement, TabsProps>(Tabs)
